import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Globe, Shield, Copy, CheckCircle, Clock, AlertCircle,
  Trash2, RefreshCw, Loader2, ArrowRight, ArrowLeft, Info, Lock, ExternalLink, Zap, Plus,
  Star, ChevronDown, ChevronUp, Settings, Save, Mail, User
} from 'lucide-react';
import { enterpriseApi } from '../services/emailApi';
import { useUserContext, canWrite } from '../../../context/userContext';
import { useOnboarding } from '../../../context/onboardingContext';
import { useToast } from '../shared/ToastProvider';
import ConfirmModal from '../shared/ConfirmModal';
import './CustomDomain.css';

/* ═══════════════════════════════════════════════════════════
   Custom Domain Page  (Settings → Email → Custom Domain)
   ═══════════════════════════════════════════════════════════
   Multi-domain support:
   - List all registered domains
   - Register new domains (won't overwrite existing)
   - Set a verified domain as active for sending
   - Expand a domain to view DNS records & status
   - Poll pending domains for verification
   ═══════════════════════════════════════════════════════════ */

const DOMAIN_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?\.[a-zA-Z]{2,}$/;
const POLL_INTERVAL = 60000; // 60 seconds

const CustomDomain = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { onboardingStatus } = useOnboarding();
  const { userCtx } = useUserContext();
  const canWriteSettings = !userCtx || !userCtx.pages || canWrite(userCtx, 'email_sender');
  const pollRef = useRef(null);

  /* ── State ──────────────────────── */
  const [loading, setLoading]               = useState(true);
  const [domains, setDomains]               = useState([]);        // array of domain objects
  const [domainError, setDomainError]       = useState(null);      // 403 or other errors
  const [polling, setPolling]               = useState(false);

  // Expanded domain (to show DNS records)
  const [expandedDomain, setExpandedDomain] = useState(null);      // domain string
  const [expandedData, setExpandedData]     = useState(null);      // DNS records data
  const [expandLoading, setExpandLoading]   = useState(false);

  // Manual check per domain
  const [checkingDomain, setCheckingDomain] = useState(null);

  // Register form
  const [showRegister, setShowRegister]     = useState(false);
  const [regDomain, setRegDomain]           = useState('');
  const [regPrefix, setRegPrefix]           = useState('campaigns');
  const [regErrors, setRegErrors]           = useState({});
  const [registering, setRegistering]       = useState(false);

  // Remove confirm
  const [showRemove, setShowRemove]         = useState(false);
  const [removingDomain, setRemovingDomain] = useState(null);      // domain string to remove
  const [removing, setRemoving]             = useState(false);

  // Set active
  const [settingActive, setSettingActive]   = useState(null);      // domain string being activated

  // Copy feedback
  const [copiedKey, setCopiedKey]           = useState(null);

  // Per-domain settings editing
  const [editingSettings, setEditingSettings] = useState(null);      // domain string being edited
  const [settingsForm, setSettingsForm]       = useState({ senderName: '', customFromPrefix: '', replyToEmail: '' });
  const [settingsErrors, setSettingsErrors]   = useState({});
  const [savingSettings, setSavingSettings]   = useState(false);

  /* ── Plan check ─────────────────── */
  const plan = onboardingStatus?.plan_name || '';
  const isEnterprise = plan?.toLowerCase() === 'enterprise';

  /* ── Fetch all domains ──────────── */
  const fetchDomains = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await enterpriseApi.listDomains();
      if (res.data?.success) {
        setDomains(res.data.data || []);
        setDomainError(null);

        // Start polling if any domain is pending
        const hasPending = (res.data.data || []).some(
          d => d.verificationStatus === 'Pending' || d.verificationStatus === 'pending'
        );
        setPolling(hasPending);
      }
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data;

      if (status === 403 && data?.code === 'ENTERPRISE_PLAN_REQUIRED') {
        setDomainError('enterprise_required');
      } else if (status === 404) {
        setDomains([]);
        setDomainError(null);
      } else {
        if (!silent) console.error('Domain list fetch error:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDomains(); }, [fetchDomains]);

  /* ── Polling for pending domains ── */
  useEffect(() => {
    if (!polling) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const res = await enterpriseApi.listDomains();
        if (res.data?.success) {
          const list = res.data.data || [];
          setDomains(list);

          const stillPending = list.some(
            d => d.verificationStatus === 'Pending' || d.verificationStatus === 'pending'
          );
          if (!stillPending) {
            setPolling(false);
            toast.success('Domain verification status updated!');
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, POLL_INTERVAL);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [polling, toast]);

  /* ── Expand domain to show DNS ──── */
  const handleExpand = async (domainName) => {
    if (expandedDomain === domainName) {
      setExpandedDomain(null);
      setExpandedData(null);
      return;
    }

    setExpandedDomain(domainName);
    setExpandLoading(true);
    try {
      const res = await enterpriseApi.getDnsRecords(domainName);
      if (res.data?.success) {
        setExpandedData(res.data.data);
      } else {
        setExpandedData(null);
      }
    } catch (err) {
      console.error('DNS records fetch error:', err);
      setExpandedData(null);
      toast.error('Failed to load DNS records');
    } finally {
      setExpandLoading(false);
    }
  };

  /* ── Manual check for a domain ──── */
  const handleManualCheck = async (domainName) => {
    setCheckingDomain(domainName);
    try {
      const res = await enterpriseApi.verifyStatus(domainName);
      if (res.data?.success) {
        const d = res.data.data;
        if (d.verified) {
          toast.success(`${domainName} verified successfully!`);
        } else if (d.status === 'Failed') {
          toast.error('Verification failed. Check DNS records and try again.');
        } else {
          toast.info('DNS records not yet propagated. This can take up to 72 hours.');
        }
        await fetchDomains(true);
        // Refresh expanded data if this domain is expanded
        if (expandedDomain === domainName) {
          const dnsRes = await enterpriseApi.getDnsRecords(domainName);
          if (dnsRes.data?.success) setExpandedData(dnsRes.data.data);
        }
      }
    } catch (err) {
      toast.error('Failed to check verification status');
    } finally {
      setCheckingDomain(null);
    }
  };

  /* ── Set active domain ──────────── */
  const handleSetActive = async (domainName) => {
    setSettingActive(domainName);
    try {
      const res = await enterpriseApi.setActiveDomain(domainName);
      if (res.data?.success) {
        toast.success(`${domainName} is now the active sending domain`);
        await fetchDomains(true);
      } else {
        toast.error(res.data?.error || 'Failed to set active domain');
      }
    } catch (err) {
      const data = err.response?.data;
      toast.error(data?.error || 'Failed to set active domain. Make sure it is verified.');
    } finally {
      setSettingActive(null);
    }
  };

  /* ── Register domain ────────────── */
  const handleRegister = async () => {
    const errs = {};
    const cleanDomain = regDomain.trim().toLowerCase()
      .replace(/^https?:\/\//i, '')
      .replace(/^www\./i, '')
      .replace(/\/.*$/, '')
      .replace(/[:?#].*$/, '')
      .trim();

    if (!cleanDomain) {
      errs.domain = 'Enter a domain name';
    } else if (!DOMAIN_REGEX.test(cleanDomain)) {
      errs.domain = 'Enter a valid domain (e.g. example.com)';
    }

    if (regPrefix && !/^[a-z0-9][a-z0-9.-]{0,48}[a-z0-9]$/.test(regPrefix)) {
      errs.prefix = 'Lowercase alphanumeric, dots, and hyphens only (2–50 chars)';
    }

    if (Object.keys(errs).length > 0) {
      setRegErrors(errs);
      return;
    }

    setRegistering(true);
    setRegErrors({});
    try {
      const res = await enterpriseApi.registerDomain(cleanDomain, regPrefix || 'campaigns');

      if (res.data?.success) {
        toast.success('Domain registered! Add the DNS records below to verify.');
        setShowRegister(false);
        setRegDomain('');
        setRegPrefix('campaigns');
        await fetchDomains();
        // Auto-expand the new domain
        setExpandedDomain(cleanDomain);
        try {
          const dnsRes = await enterpriseApi.getDnsRecords(cleanDomain);
          if (dnsRes.data?.success) setExpandedData(dnsRes.data.data);
        } catch { /* ignore */ }
      } else {
        toast.error(res.data?.error || 'Failed to register domain');
      }
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === 'DOMAIN_ALREADY_EXISTS') {
        toast.error('This domain is already registered');
        setRegErrors({ domain: 'Domain already registered' });
      } else if (data?.code === 'ENTERPRISE_PLAN_REQUIRED') {
        toast.error('Enterprise plan required for custom domains');
      } else {
        toast.error(data?.error || 'Failed to register domain');
      }
    } finally {
      setRegistering(false);
    }
  };

  /* ── Remove domain ──────────────── */
  const handleRemove = async () => {
    if (!removingDomain) return;
    setRemoving(true);
    try {
      const res = await enterpriseApi.removeDomain(removingDomain);
      if (res.data?.success) {
        toast.success(`${removingDomain} removed.`);
        setShowRemove(false);
        setRemovingDomain(null);
        if (expandedDomain === removingDomain) {
          setExpandedDomain(null);
          setExpandedData(null);
        }
        await fetchDomains();
      } else {
        toast.error(res.data?.error || 'Failed to remove domain');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove domain');
    } finally {
      setRemoving(false);
    }
  };

  /* ── Open domain settings form ──── */
  const handleOpenSettings = (d) => {
    // If already editing this domain, toggle off
    if (editingSettings === d.domain) {
      setEditingSettings(null);
      setSettingsForm({ senderName: '', customFromPrefix: '', replyToEmail: '' });
      setSettingsErrors({});
      return;
    }
    // Pre-fill with existing values from the domain object
    setSettingsForm({
      senderName:      d.senderName || d.sender_name || '',
      customFromPrefix: d.customFromPrefix || d.custom_from_prefix || '',
      replyToEmail:    d.replyToEmail || d.reply_to_email || '',
    });
    setSettingsErrors({});
    setEditingSettings(d.domain);
  };

  /* ── Save domain settings ───────── */
  const handleSaveSettings = async (domainName) => {
    const errs = {};
    const name = settingsForm.senderName.trim();
    const prefix = settingsForm.customFromPrefix.trim().toLowerCase();
    const replyTo = settingsForm.replyToEmail.trim();

    if (name && name.length > 100) {
      errs.senderName = 'Maximum 100 characters';
    }
    if (prefix && !/^[a-z0-9][a-z0-9.-]{0,48}[a-z0-9]$/.test(prefix) && prefix.length > 1) {
      errs.customFromPrefix = 'Lowercase alphanumeric, dots, and hyphens only (2–50 chars)';
    }
    if (replyTo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(replyTo)) {
      errs.replyToEmail = 'Enter a valid email address';
    }

    if (Object.keys(errs).length > 0) {
      setSettingsErrors(errs);
      return;
    }

    setSavingSettings(true);
    try {
      const payload = { domain: domainName };
      if (name) payload.senderName = name;
      if (prefix) payload.customFromPrefix = prefix;
      if (replyTo) payload.replyToEmail = replyTo;

      const res = await enterpriseApi.updateDomainSettings(payload);
      if (res.data?.success) {
        toast.success('Domain settings saved successfully');
        setEditingSettings(null);
        setSettingsForm({ senderName: '', customFromPrefix: '', replyToEmail: '' });
        setSettingsErrors({});
        // Refresh domain list to reflect new values
        await fetchDomains(true);
        // Refresh expanded data if this domain is expanded
        if (expandedDomain === domainName) {
          try {
            const dnsRes = await enterpriseApi.getDnsRecords(domainName);
            if (dnsRes.data?.success) setExpandedData(dnsRes.data.data);
          } catch { /* ignore */ }
        }
      } else {
        toast.error(res.data?.error || 'Failed to save domain settings');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save domain settings');
    } finally {
      setSavingSettings(false);
    }
  };

  /* ── Copy to clipboard ──────────── */
  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  /* ── Status helpers ─────────────── */
  const getStatusConfig = (status) => {
    switch (status) {
      case 'Success':
      case 'verified':
        return { label: 'Verified', badge: 'success', icon: <CheckCircle size={14} />, color: 'var(--em-success)' };
      case 'Pending':
      case 'pending':
        return { label: 'Pending', badge: 'warning', icon: <Clock size={14} />, color: 'var(--em-warning)' };
      case 'Failed':
      case 'failed':
        return { label: 'Failed', badge: 'danger', icon: <AlertCircle size={14} />, color: 'var(--em-danger)' };
      default:
        return { label: status || 'Unknown', badge: 'neutral', icon: <Clock size={14} />, color: 'var(--em-text-3)' };
    }
  };

  const isVerified = (d) => d.verificationStatus === 'Success' || d.verificationStatus === 'verified';
  const isPending  = (d) => d.verificationStatus === 'Pending' || d.verificationStatus === 'pending';

  /* ═══════════════════════════════════════════ */
  /*  RENDER                                     */
  /* ═══════════════════════════════════════════ */

  /* ── Enterprise gate view ───────── */
  if (!loading && (domainError === 'enterprise_required' || !isEnterprise)) {
    return (
      <div className="cd-page">
        <div className="cd-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => navigate(-1)} title="Go back">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="cd-title">Custom Domain</h1>
              <p className="cd-subtitle">Send emails from your own domain</p>
            </div>
          </div>
        </div>

        <div className="cd-gate-card">
          <div className="cd-gate-icon">
            <Lock size={40} />
          </div>
          <h2 className="cd-gate-title">Enterprise Feature</h2>
          <p className="cd-gate-text">
            Custom domain verification allows you to send emails directly from your own domain
            (e.g., <strong>campaigns@yourdomain.com</strong>) instead of the shared platform domain.
            This improves deliverability and brand trust.
          </p>
          <div className="cd-gate-benefits">
            <div className="cd-gate-benefit">
              <Shield size={16} />
              <span>Better email deliverability</span>
            </div>
            <div className="cd-gate-benefit">
              <Globe size={16} />
              <span>Send from your own domain</span>
            </div>
            <div className="cd-gate-benefit">
              <Zap size={16} />
              <span>Enhanced brand trust</span>
            </div>
          </div>
          <a href="/dashboard/pricing" className="em-btn em-btn-primary cd-gate-btn">
            Upgrade to Enterprise
            <ArrowRight size={16} />
          </a>
          <p className="em-helper" style={{ marginTop: 12 }}>
            Currently on: <strong>{plan || 'Normal'}</strong> plan
          </p>
        </div>
      </div>
    );
  }

  /* ── Loading ────────────────────── */
  if (loading) {
    return (
      <div className="cd-page">
        <div className="cd-header">
          <div className="em-skl" style={{ width: 200, height: 28 }} />
        </div>
        <div className="em-card" style={{ padding: '3rem 2rem', textAlign: 'center' }}>
          <div className="em-skl" style={{ width: 300, height: 20, margin: '0 auto 12px' }} />
          <div className="em-skl" style={{ width: 240, height: 14, margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  /* ── Main View ──────────────────── */
  return (
    <div className="cd-page">
      {/* Header */}
      <div className="cd-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => navigate(-1)} title="Go back">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="cd-title">Custom Domains</h1>
            <p className="cd-subtitle">
              {domains.length > 0
                ? `${domains.length} domain${domains.length !== 1 ? 's' : ''} registered`
                : 'Send emails from your own verified domain'}
            </p>
          </div>
        </div>
        <button className="em-btn em-btn-primary em-btn-sm" onClick={() => setShowRegister(true)} disabled={!canWriteSettings}>
          <Plus size={14} />
          Add Domain
        </button>
      </div>

      {/* Register Form (inline card) */}
      {showRegister && (
        <div className="em-card cd-register-card" style={{ marginBottom: '1.25rem' }}>
          <div className="em-card-header">
            <div>
              <h2 className="em-card-title">Register New Domain</h2>
              <p className="em-card-sub">Enter your domain to begin verification</p>
            </div>
            <button className="em-btn em-btn-ghost em-btn-sm" onClick={() => setShowRegister(false)}>
              Cancel
            </button>
          </div>

          <div className="em-input-group cd-field">
            <label className="em-label">Domain Name</label>
            <input
              className={`em-input ${regErrors.domain ? 'em-input-error' : ''}`}
              type="text"
              value={regDomain}
              onChange={e => { setRegDomain(e.target.value); setRegErrors(p => ({ ...p, domain: null })); }}
              placeholder="e.g. rajeshstores.com"
            />
            {regErrors.domain
              ? <p className="em-error-text">{regErrors.domain}</p>
              : <p className="em-helper">The domain you own and want to send emails from</p>
            }
          </div>

          <div className="em-input-group cd-field">
            <label className="em-label">From Prefix</label>
            <div className="sp-handle-input">
              <input
                className={`em-input sp-handle-field ${regErrors.prefix ? 'em-input-error' : ''}`}
                type="text"
                value={regPrefix}
                onChange={e => { setRegPrefix(e.target.value.toLowerCase()); setRegErrors(p => ({ ...p, prefix: null })); }}
                placeholder="campaigns"
              />
              <span className="sp-handle-suffix">@{regDomain || 'yourdomain.com'}</span>
            </div>
            {regErrors.prefix
              ? <p className="em-error-text">{regErrors.prefix}</p>
              : <p className="em-helper">The prefix used in the From address (e.g. campaigns, info, noreply)</p>
            }
          </div>

          <div className="cd-register-preview">
            <Info size={14} />
            <span>
              Your emails will be sent from: <strong>{regPrefix || 'campaigns'}@{regDomain || 'yourdomain.com'}</strong>
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button className="em-btn em-btn-secondary" onClick={() => setShowRegister(false)}>
              Cancel
            </button>
            <button className="em-btn em-btn-primary" onClick={handleRegister} disabled={registering || !canWriteSettings}>
              {registering ? <Loader2 size={16} className="spin-icon" /> : <Globe size={16} />}
              {registering ? 'Registering...' : 'Register Domain'}
            </button>
          </div>
        </div>
      )}

      {/* Domain List */}
      {domains.length === 0 && !showRegister ? (
        <div className="em-card cd-empty-state">
          <div className="em-empty">
            <div className="em-empty-icon">
              <Globe size={28} />
            </div>
            <h3>No Custom Domain Configured</h3>
            <p>
              Register your domain to send emails from your own address instead of the shared platform domain.
            </p>
            <button className="em-btn em-btn-primary" onClick={() => setShowRegister(true)} disabled={!canWriteSettings}>
              <Globe size={16} />
              Register Domain
            </button>
          </div>

          <div className="cd-how-it-works">
            <h4 className="cd-how-title">How it works</h4>
            <div className="cd-steps">
              <div className="cd-step">
                <div className="cd-step-num">1</div>
                <div className="cd-step-info">
                  <span className="cd-step-label">Register</span>
                  <span className="cd-step-desc">Enter your domain name</span>
                </div>
              </div>
              <ArrowRight size={16} className="cd-step-arrow" />
              <div className="cd-step">
                <div className="cd-step-num">2</div>
                <div className="cd-step-info">
                  <span className="cd-step-label">Add DNS</span>
                  <span className="cd-step-desc">Add CNAME & TXT records</span>
                </div>
              </div>
              <ArrowRight size={16} className="cd-step-arrow" />
              <div className="cd-step">
                <div className="cd-step-num">3</div>
                <div className="cd-step-info">
                  <span className="cd-step-label">Verify</span>
                  <span className="cd-step-desc">Auto-verified in 10–72 hrs</span>
                </div>
              </div>
              <ArrowRight size={16} className="cd-step-arrow" />
              <div className="cd-step">
                <div className="cd-step-num">4</div>
                <div className="cd-step-info">
                  <span className="cd-step-label">Send</span>
                  <span className="cd-step-desc">Emails use your domain</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : domains.length > 0 && (
        <div className="cd-domain-list">
          {domains.map((d) => {
            const st = getStatusConfig(d.verificationStatus);
            const dkimSt = d.dkimStatus ? getStatusConfig(d.dkimStatus) : null;
            const isExp = expandedDomain === d.domain;
            const verified = isVerified(d);
            const pending = isPending(d);

            return (
              <div key={d.domain} className={`em-card cd-domain-card ${d.isActive ? 'cd-domain-card--active' : ''}`}>
                {/* Domain Row */}
                <div className="cd-domain-row" onClick={() => handleExpand(d.domain)}>
                  <div className="cd-domain-main">
                    <div className="cd-domain-icon" style={{
                      background: verified ? 'var(--em-success-l, #dcfce7)' : pending ? 'var(--em-warning-l, #fef3c7)' : 'var(--em-danger-l, #fee2e2)',
                      color: verified ? 'var(--em-success)' : pending ? 'var(--em-warning)' : 'var(--em-danger)'
                    }}>
                      <Globe size={18} />
                    </div>
                    <div className="cd-domain-info">
                      <div className="cd-domain-name">
                        <span className="em-font-mono">{d.domain}</span>
                        {d.isActive && (
                          <span className="em-badge em-badge-primary" style={{ fontSize: '0.6rem', padding: '1px 6px', marginLeft: 6 }}>
                            <Star size={10} style={{ marginRight: 2 }} /> Active
                          </span>
                        )}
                      </div>
                      <div className="cd-domain-meta">
                        <span className={`em-badge em-badge-${st.badge}`} style={{ fontSize: '0.65rem' }}>
                          {st.icon} {st.label}
                        </span>
                        {dkimSt && (
                          <span className={`em-badge em-badge-${dkimSt.badge}`} style={{ fontSize: '0.65rem' }}>
                            DKIM: {dkimSt.label}
                          </span>
                        )}
                        {d.customFromPrefix && (
                          <span className="em-text-xs em-text-muted">
                            Prefix: <span className="em-font-mono">{d.customFromPrefix}</span>
                          </span>
                        )}
                        {d.senderName && (
                          <span className="em-text-xs em-text-muted">
                            Sender: {d.senderName}
                          </span>
                        )}
                        {pending && polling && (
                          <span className="cd-polling-indicator"><Loader2 size={10} className="spin-icon" /> Auto-checking</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="cd-domain-actions" onClick={e => e.stopPropagation()}>
                    {/* Set Active — only for verified, non-active domains */}
                    {verified && !d.isActive && canWriteSettings && (
                      <button
                        className="em-btn em-btn-primary em-btn-sm"
                        onClick={() => handleSetActive(d.domain)}
                        disabled={settingActive === d.domain}
                        title="Set as active sending domain"
                      >
                        {settingActive === d.domain
                          ? <Loader2 size={13} className="spin-icon" />
                          : <Star size={13} />}
                        Set Active
                      </button>
                    )}
                    {/* Check Now */}
                    {!verified && (
                      <button
                        className="em-btn em-btn-secondary em-btn-sm"
                        onClick={() => handleManualCheck(d.domain)}
                        disabled={checkingDomain === d.domain}
                        title="Check verification status"
                      >
                        {checkingDomain === d.domain
                          ? <Loader2 size={13} className="spin-icon" />
                          : <RefreshCw size={13} />}
                        Check
                      </button>
                    )}
                    {/* Domain Settings — only for verified domains */}
                    {verified && canWriteSettings && (
                      <button
                        className={`em-btn em-btn-ghost em-btn-icon em-btn-sm ${editingSettings === d.domain ? 'cd-settings-btn--active' : ''}`}
                        onClick={() => handleOpenSettings(d)}
                        title="Domain sending settings"
                      >
                        <Settings size={14} />
                      </button>
                    )}
                    {/* Remove */}
                    {canWriteSettings && (
                    <button
                      className="em-btn em-btn-ghost em-btn-icon em-btn-sm"
                      style={{ color: 'var(--em-danger)' }}
                      onClick={() => { setRemovingDomain(d.domain); setShowRemove(true); }}
                      title="Remove domain"
                    >
                      <Trash2 size={14} />
                    </button>
                    )}
                    {/* Expand chevron */}
                    <button className="em-btn em-btn-ghost em-btn-icon em-btn-sm" onClick={() => handleExpand(d.domain)}>
                      {isExp ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                </div>

                {/* Domain Settings Panel — slides in when settings icon is clicked */}
                {editingSettings === d.domain && (
                  <div className="cd-settings-panel">
                    <div className="cd-settings-header">
                      <div className="cd-settings-header-left">
                        <Settings size={16} />
                        <div>
                          <h3 className="cd-settings-title">Domain Settings</h3>
                          <p className="cd-settings-sub">Customize how emails appear when sent from <strong>{d.domain}</strong></p>
                        </div>
                      </div>
                    </div>

                    <div className="cd-settings-fields">
                      <div className="cd-settings-field">
                        <label className="em-label">
                          <User size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
                          Sender Name
                        </label>
                        <input
                          className={`em-input ${settingsErrors.senderName ? 'em-input-error' : ''}`}
                          type="text"
                          value={settingsForm.senderName}
                          onChange={e => { setSettingsForm(p => ({ ...p, senderName: e.target.value })); setSettingsErrors(p => ({ ...p, senderName: null })); }}
                          placeholder="e.g. My Brand, Company Name"
                          maxLength={100}
                        />
                        {settingsErrors.senderName
                          ? <p className="em-error-text">{settingsErrors.senderName}</p>
                          : <p className="em-helper">The display name recipients see (e.g. "KFC" instead of "MessageOS")</p>
                        }
                      </div>

                      <div className="cd-settings-field">
                        <label className="em-label">
                          <Mail size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
                          From Prefix
                        </label>
                        <div className="sp-handle-input">
                          <input
                            className={`em-input sp-handle-field ${settingsErrors.customFromPrefix ? 'em-input-error' : ''}`}
                            type="text"
                            value={settingsForm.customFromPrefix}
                            onChange={e => { setSettingsForm(p => ({ ...p, customFromPrefix: e.target.value.toLowerCase() })); setSettingsErrors(p => ({ ...p, customFromPrefix: null })); }}
                            placeholder="info"
                          />
                          <span className="sp-handle-suffix">@{d.domain}</span>
                        </div>
                        {settingsErrors.customFromPrefix
                          ? <p className="em-error-text">{settingsErrors.customFromPrefix}</p>
                          : <p className="em-helper">The part before @ in the From address (e.g. campaigns, info, noreply)</p>
                        }
                      </div>

                      <div className="cd-settings-field">
                        <label className="em-label">
                          <Mail size={13} style={{ marginRight: 4, verticalAlign: -2 }} />
                          Reply-To Email
                        </label>
                        <input
                          className={`em-input ${settingsErrors.replyToEmail ? 'em-input-error' : ''}`}
                          type="email"
                          value={settingsForm.replyToEmail}
                          onChange={e => { setSettingsForm(p => ({ ...p, replyToEmail: e.target.value })); setSettingsErrors(p => ({ ...p, replyToEmail: null })); }}
                          placeholder="e.g. support@yourdomain.com"
                        />
                        {settingsErrors.replyToEmail
                          ? <p className="em-error-text">{settingsErrors.replyToEmail}</p>
                          : <p className="em-helper">Where replies go when recipients hit "Reply" (leave blank to use the From address)</p>
                        }
                      </div>
                    </div>

                    {/* Live preview */}
                    <div className="cd-settings-preview">
                      <Info size={14} />
                      <span>
                        Recipients will see: <strong>
                          {settingsForm.senderName || 'Your Brand'}
                          {' <'}{settingsForm.customFromPrefix || d.customFromPrefix || 'campaigns'}@{d.domain}{'>'}
                        </strong>
                      </span>
                    </div>

                    <div className="cd-settings-actions">
                      <button
                        className="em-btn em-btn-secondary em-btn-sm"
                        onClick={() => { setEditingSettings(null); setSettingsErrors({}); }}
                        disabled={savingSettings}
                      >
                        Cancel
                      </button>
                      <button
                        className="em-btn em-btn-primary em-btn-sm"
                        onClick={() => handleSaveSettings(d.domain)}
                        disabled={savingSettings}
                      >
                        {savingSettings ? <Loader2 size={14} className="spin-icon" /> : <Save size={14} />}
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Expanded: DNS Records + Status Details */}
                {isExp && (
                  <div className="cd-domain-expanded">
                    {expandLoading ? (
                      <div className="cd-expand-loading">
                        <Loader2 size={16} className="spin-icon" />
                        <span>Loading DNS records...</span>
                      </div>
                    ) : expandedData ? (
                      <>
                        {/* Status Details */}
                        <div className="cd-status-grid" style={{ marginBottom: '1rem' }}>
                          <div className="cd-status-item">
                            <span className="cd-status-label">Verification</span>
                            <span className={`em-badge em-badge-${st.badge}`}>{st.icon} {st.label}</span>
                          </div>
                          {expandedData.dkimStatus && (
                            <div className="cd-status-item">
                              <span className="cd-status-label">DKIM</span>
                              <span className={`em-badge em-badge-${getStatusConfig(expandedData.dkimStatus).badge}`}>
                                {getStatusConfig(expandedData.dkimStatus).icon} {getStatusConfig(expandedData.dkimStatus).label}
                              </span>
                            </div>
                          )}
                          <div className="cd-status-item">
                            <span className="cd-status-label">From Prefix</span>
                            <span className="cd-status-value em-font-mono">{expandedData.customFromPrefix || d.customFromPrefix || 'campaigns'}</span>
                          </div>
                          {(d.senderName || expandedData.senderName) && (
                            <div className="cd-status-item">
                              <span className="cd-status-label">Sender Name</span>
                              <span className="cd-status-value">{d.senderName || expandedData.senderName}</span>
                            </div>
                          )}
                          {(d.replyToEmail || expandedData.replyToEmail) && (
                            <div className="cd-status-item">
                              <span className="cd-status-label">Reply-To</span>
                              <span className="cd-status-value em-font-mono">{d.replyToEmail || expandedData.replyToEmail}</span>
                            </div>
                          )}
                          {(expandedData.createdAt || d.createdAt) && (
                            <div className="cd-status-item">
                              <span className="cd-status-label">Registered</span>
                              <span className="cd-status-value">{new Date(expandedData.createdAt || d.createdAt).toLocaleString()}</span>
                            </div>
                          )}
                        </div>

                        {/* Banners */}
                        {verified && (
                          <div className="cd-banner cd-banner-success" style={{ marginBottom: '1rem' }}>
                            <CheckCircle size={18} />
                            <div>
                              <strong>Domain Verified!</strong>
                              <p>{d.isActive
                                ? <>This is your active sending domain. Campaigns send from <strong>{d.senderName ? `${d.senderName} <` : ''}{d.customFromPrefix || 'campaigns'}@{d.domain}{d.senderName ? '>' : ''}</strong></>
                                : <>Domain is verified. Click "Set Active" to start sending from <strong>{d.customFromPrefix || 'campaigns'}@{d.domain}</strong></>
                              }</p>
                            </div>
                          </div>
                        )}
                        {pending && (
                          <div className="cd-banner cd-banner-pending" style={{ marginBottom: '1rem' }}>
                            <Clock size={18} />
                            <div>
                              <strong>Verification in Progress</strong>
                              <p>DNS propagation can take up to 72 hours. We check automatically every 60 seconds.</p>
                            </div>
                          </div>
                        )}
                        {(expandedData.verificationStatus === 'Failed' || d.verificationStatus === 'Failed') && (
                          <div className="cd-banner cd-banner-failed" style={{ marginBottom: '1rem' }}>
                            <AlertCircle size={18} />
                            <div>
                              <strong>Verification Failed</strong>
                              <p>Please check that the DNS records below are correctly added and try again.</p>
                            </div>
                          </div>
                        )}

                        {/* DNS Records Table */}
                        {(() => {
                          const records = expandedData.records || expandedData.dnsRecords || [];
                          if (records.length === 0) return null;
                          return (
                            <div className="cd-dns-section">
                              <h3 className="cd-dns-section-title">DNS Records — Add to your registrar</h3>
                              <div className="em-table-wrap">
                                <table className="em-table cd-dns-table">
                                  <thead>
                                    <tr>
                                      <th style={{ width: 60 }}>Type</th>
                                      <th>Name / Host</th>
                                      <th>Value / Points To</th>
                                      <th style={{ width: 100 }}>Purpose</th>
                                      <th style={{ width: 50 }}></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {records.map((rec, idx) => {
                                      const copyKey = `${d.domain}-${idx}`;
                                      return (
                                        <tr key={idx}>
                                          <td>
                                            <span className={`em-badge ${rec.type === 'TXT' ? 'em-badge-info' : 'em-badge-primary'}`}>
                                              {rec.type}
                                            </span>
                                          </td>
                                          <td><code className="cd-dns-code">{rec.name}</code></td>
                                          <td><code className="cd-dns-code cd-dns-value">{rec.value}</code></td>
                                          <td><span className="em-text-sm em-text-muted">{rec.purpose}</span></td>
                                          <td>
                                            <button
                                              className="em-btn em-btn-ghost em-btn-icon em-btn-sm"
                                              onClick={() => copyToClipboard(rec.value, copyKey)}
                                              title="Copy value"
                                            >
                                              {copiedKey === copyKey
                                                ? <CheckCircle size={14} style={{ color: 'var(--em-success)' }} />
                                                : <Copy size={14} />}
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>

                              <div className="cd-dns-help">
                                <Info size={14} />
                                <div>
                                  <p><strong>How to add DNS records:</strong></p>
                                  <ol>
                                    <li>Log into your domain registrar (GoDaddy, Cloudflare, Namecheap, etc.)</li>
                                    <li>Navigate to DNS settings for <strong>{d.domain}</strong></li>
                                    <li>Add each record above with the exact Type, Name, and Value</li>
                                    <li>Save changes and wait for propagation (usually 10–30 min, up to 72 hours)</li>
                                  </ol>
                                  <a href="https://docs.aws.amazon.com/ses/latest/dg/creating-identities.html" target="_blank" rel="noopener noreferrer" className="cd-dns-link">
                                    <ExternalLink size={12} />
                                    AWS SES Domain Verification Documentation
                                  </a>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <p className="em-text-sm em-text-muted" style={{ padding: '1rem 0' }}>
                        No DNS records available for this domain.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Remove Domain Confirm */}
      <ConfirmModal
        open={showRemove}
        onCancel={() => { setShowRemove(false); setRemovingDomain(null); }}
        onConfirm={handleRemove}
        title="Remove Custom Domain?"
        message={`This will remove "${removingDomain}" from SES. If it was the active domain, the system will fall back to another verified domain or the shared platform domain. This action cannot be undone.`}
        confirmLabel="Remove Domain"
        variant="danger"
        loading={removing}
      />
    </div>
  );
};

export default CustomDomain;
